'use client'

import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { fmt, roleVar, type Role } from './controls'

/**
 * Declarative SVG coordinate plane. Children draw in data coordinates via
 * the context's scale functions; <Pt drag> handles pointer + keyboard drags.
 */

type Scale = {
  sx: (x: number) => number
  sy: (y: number) => number
  ix: (px: number) => number
  iy: (py: number) => number
  x: [number, number]
  y: [number, number]
  w: number
  h: number
}

const PlotCtx = createContext<Scale | null>(null)
// Kept apart from the scale so render-time drawing never touches a ref;
// only pointer handlers read it (to map screen → data coordinates).
const PlotSvgCtx = createContext<React.RefObject<SVGSVGElement | null> | null>(null)

export function usePlot(): Scale {
  const ctx = useContext(PlotCtx)
  if (!ctx) throw new Error('Plot primitives must be inside <Plot>')
  return ctx
}

function clientToSvg(svg: SVGSVGElement | null | undefined, clientX: number, clientY: number): { x: number; y: number } | null {
  if (!svg) return null
  const m = svg.getScreenCTM()
  if (!m) return null
  const pt = svg.createSVGPoint()
  pt.x = clientX
  pt.y = clientY
  const p = pt.matrixTransform(m.inverse())
  return { x: p.x, y: p.y }
}

function niceStep(span: number, target = 8): number {
  const raw = span / target
  const pow = Math.pow(10, Math.floor(Math.log10(raw)))
  const n = raw / pow
  return (n < 1.5 ? 1 : n < 3 ? 2 : n < 7 ? 5 : 10) * pow
}

export function Plot({
  x,
  y,
  width: designWidth = 640,
  height: designHeight,
  grid = true,
  axes = 'cross',
  ticks = true,
  xStep,
  yStep,
  xLabel,
  yLabel,
  children,
  className = '',
  ariaLabel = 'Interactive graph',
  onPointer,
}: {
  x: [number, number]
  y: [number, number]
  width?: number
  /** Defaults to equal scaling on both axes. */
  height?: number
  grid?: boolean
  axes?: 'cross' | 'box' | 'none'
  ticks?: boolean
  xStep?: number
  yStep?: number
  xLabel?: ReactNode
  yLabel?: ReactNode
  children?: ReactNode
  className?: string
  ariaLabel?: string
  /** Pointer position in data coordinates (null on leave). */
  onPointer?: (p: { x: number; y: number; down: boolean } | null) => void
}) {
  const svg = useRef<SVGSVGElement>(null)
  // Lay out at the real on-screen width when it's narrower than the design
  // width, so labels keep their true pixel size on phones instead of shrinking.
  const [measured, setMeasured] = useState<number | null>(null)
  useEffect(() => {
    const el = svg.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(entries => {
      const cw = Math.round(entries[0].contentRect.width)
      if (cw > 0) setMeasured(cw)
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  const width = measured ? Math.min(designWidth, Math.max(300, measured)) : designWidth
  const height = designHeight !== undefined ? Math.round((designHeight * width) / designWidth) : undefined
  const h = height ?? Math.round((width * (y[1] - y[0])) / (x[1] - x[0]))
  const pad = axes === 'box' ? { l: 44, r: 12, t: 12, b: 30 } : { l: 0, r: 0, t: 0, b: 0 }
  const iw = width - pad.l - pad.r
  const ih = h - pad.t - pad.b

  const scale = useMemo<Scale>(() => {
    const sx = (v: number) => pad.l + ((v - x[0]) / (x[1] - x[0])) * iw
    const sy = (v: number) => pad.t + ih - ((v - y[0]) / (y[1] - y[0])) * ih
    const ix = (px: number) => x[0] + ((px - pad.l) / iw) * (x[1] - x[0])
    const iy = (py: number) => y[0] + ((pad.t + ih - py) / ih) * (y[1] - y[0])
    return { sx, sy, ix, iy, x, y, w: width, h }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [x[0], x[1], y[0], y[1], width, h, iw, ih, pad.l, pad.t])

  const xs = xStep ?? niceStep(x[1] - x[0])
  const ys = yStep ?? niceStep(y[1] - y[0], Math.max(4, Math.round((8 * ih) / iw)))
  const xTicks: number[] = []
  for (let v = Math.ceil(x[0] / xs) * xs; v <= x[1] + 1e-9; v += xs) xTicks.push(+v.toFixed(10))
  const yTicks: number[] = []
  for (let v = Math.ceil(y[0] / ys) * ys; v <= y[1] + 1e-9; v += ys) yTicks.push(+v.toFixed(10))

  const { sx, sy } = scale
  const x0 = Math.min(Math.max(0, x[0]), x[1])
  const y0 = Math.min(Math.max(0, y[0]), y[1])

  function toData(e: React.PointerEvent) {
    const p = clientToSvg(e.currentTarget as SVGSVGElement, e.clientX, e.clientY)
    return p ? { x: scale.ix(p.x), y: scale.iy(p.y) } : null
  }

  return (
    <PlotSvgCtx.Provider value={svg}>
    <PlotCtx.Provider value={scale}>
      <svg
        ref={svg}
        viewBox={`0 0 ${width} ${h}`}
        className={`gd-plot ${className}`}
        role="img"
        aria-label={ariaLabel}
        onPointerMove={onPointer ? e => { const p = toData(e); if (p) onPointer({ ...p, down: e.buttons > 0 }) } : undefined}
        onPointerDown={onPointer ? e => { const p = toData(e); if (p) onPointer({ ...p, down: true }) } : undefined}
        onPointerLeave={onPointer ? () => onPointer(null) : undefined}
      >
        {grid && (
          <g stroke="var(--gd-grid)" strokeWidth={1}>
            {xTicks.map(t => (
              <line key={`gx${t}`} x1={sx(t)} x2={sx(t)} y1={pad.t} y2={pad.t + ih} />
            ))}
            {yTicks.map(t => (
              <line key={`gy${t}`} y1={sy(t)} y2={sy(t)} x1={pad.l} x2={pad.l + iw} />
            ))}
          </g>
        )}

        {axes === 'cross' && (
          <g>
            <line x1={pad.l} x2={pad.l + iw} y1={sy(y0)} y2={sy(y0)} stroke="var(--gd-ink-3)" strokeWidth={1.2} />
            <line y1={pad.t} y2={pad.t + ih} x1={sx(x0)} x2={sx(x0)} stroke="var(--gd-ink-3)" strokeWidth={1.2} />
            {ticks && (
              <g fontSize={11} fill="var(--gd-ink-3)">
                {xTicks
                  .filter(t => Math.abs(t - x0) > 1e-9 && sx(t) > 10 && sx(t) < width - 10)
                  .map(t => (
                    <text key={`tx${t}`} x={sx(t)} y={Math.min(sy(y0) + 15, h - 4)} textAnchor="middle">
                      {fmt(t, 2)}
                    </text>
                  ))}
                {yTicks
                  .filter(t => Math.abs(t - y0) > 1e-9 && sy(t) > 10 && sy(t) < h - 6)
                  .map(t => (
                    <text key={`ty${t}`} x={Math.max(sx(x0) - 6, 26)} y={sy(t) + 4} textAnchor="end">
                      {fmt(t, 2)}
                    </text>
                  ))}
              </g>
            )}
          </g>
        )}

        {axes === 'box' && (
          <g>
            <rect x={pad.l} y={pad.t} width={iw} height={ih} fill="none" stroke="var(--gd-rule-2)" />
            {ticks && (
              <g fontSize={11} fill="var(--gd-ink-3)">
                {xTicks.map(t => (
                  <text key={`bx${t}`} x={sx(t)} y={pad.t + ih + 18} textAnchor="middle">
                    {fmt(t, 2)}
                  </text>
                ))}
                {yTicks.map(t => (
                  <text key={`by${t}`} x={pad.l - 8} y={sy(t) + 4} textAnchor="end">
                    {fmt(t, 2)}
                  </text>
                ))}
              </g>
            )}
          </g>
        )}

        {xLabel && (
          <text x={pad.l + iw - 4} y={axes === 'cross' ? sy(y0) - 8 : pad.t + ih - 6} textAnchor="end" fontSize={12} fill="var(--gd-ink-2)" fontStyle="italic">
            {xLabel}
          </text>
        )}
        {yLabel && (
          <text x={(axes === 'cross' ? sx(x0) : pad.l) + 8} y={pad.t + 14} fontSize={12} fill="var(--gd-ink-2)" fontStyle="italic">
            {yLabel}
          </text>
        )}

        {children}
      </svg>
    </PlotCtx.Provider>
    </PlotSvgCtx.Provider>
  )
}

// ─── Primitives ────────────────────────────────────────────────────────────────

type Stroke = { color?: Role | string; width?: number; dashed?: boolean; opacity?: number }

function colorOf(c: Role | string | undefined, fallback: Role = 'ink'): string {
  if (!c) return roleVar(fallback)
  return c.startsWith('#') || c.startsWith('var(') || c.startsWith('rgb') || c === 'none' ? c : roleVar(c as Role)
}

/** Plot y = f(x) across the visible x-range (breaks at discontinuities / off-screen). */
export function FnPath({ f, color = 'amber', width = 2.5, dashed, opacity = 1, from, to, samples = 400 }: Stroke & { f: (x: number) => number; from?: number; to?: number; samples?: number }) {
  const { sx, sy, x, y } = usePlot()
  const a = from ?? x[0]
  const b = to ?? x[1]
  const span = y[1] - y[0]
  let d = ''
  let pen = false
  for (let i = 0; i <= samples; i++) {
    const xv = a + ((b - a) * i) / samples
    const yv = f(xv)
    if (!Number.isFinite(yv) || yv > y[1] + span * 2 || yv < y[0] - span * 2) {
      pen = false
      continue
    }
    d += `${pen ? 'L' : 'M'}${sx(xv).toFixed(2)},${sy(yv).toFixed(2)}`
    pen = true
  }
  return <path d={d} fill="none" stroke={colorOf(color)} strokeWidth={width} strokeDasharray={dashed ? '6 6' : undefined} opacity={opacity} strokeLinecap="round" strokeLinejoin="round" />
}

export function Seg({ x1, y1, x2, y2, color = 'ink', width = 2, dashed, opacity = 1 }: Stroke & { x1: number; y1: number; x2: number; y2: number }) {
  const { sx, sy } = usePlot()
  return <line x1={sx(x1)} y1={sy(y1)} x2={sx(x2)} y2={sy(y2)} stroke={colorOf(color)} strokeWidth={width} strokeDasharray={dashed ? '5 5' : undefined} opacity={opacity} strokeLinecap="round" />
}

/** Infinite line through two points, clipped to the view. */
export function InfLine({ x1, y1, x2, y2, ...rest }: Stroke & { x1: number; y1: number; x2: number; y2: number }) {
  const { x } = usePlot()
  if (Math.abs(x2 - x1) < 1e-12) return <Seg x1={x1} y1={-1e6} x2={x1} y2={1e6} {...rest} />
  const m = (y2 - y1) / (x2 - x1)
  return <Seg x1={x[0]} y1={y1 + m * (x[0] - x1)} x2={x[1]} y2={y1 + m * (x[1] - x1)} {...rest} />
}

export function Arrow({ x1, y1, x2, y2, color = 'blue', width = 2.5, head = 10, label, labelOffset = [8, -8], dashed, opacity = 1 }: Stroke & { x1: number; y1: number; x2: number; y2: number; head?: number; label?: ReactNode; labelOffset?: [number, number] }) {
  const { sx, sy } = usePlot()
  const ax = sx(x1)
  const ay = sy(y1)
  const bx = sx(x2)
  const by = sy(y2)
  const len = Math.hypot(bx - ax, by - ay)
  const c = colorOf(color)
  if (len < 1) return null
  const ux = (bx - ax) / len
  const uy = (by - ay) / len
  const hl = Math.min(head, len * 0.5)
  const tipBack = { x: bx - ux * hl, y: by - uy * hl }
  const p1 = `${tipBack.x - uy * hl * 0.5},${tipBack.y + ux * hl * 0.5}`
  const p2 = `${tipBack.x + uy * hl * 0.5},${tipBack.y - ux * hl * 0.5}`
  return (
    <g opacity={opacity}>
      <line x1={ax} y1={ay} x2={tipBack.x + ux * 1} y2={tipBack.y + uy * 1} stroke={c} strokeWidth={width} strokeLinecap="round" strokeDasharray={dashed ? '5 5' : undefined} />
      <polygon points={`${bx},${by} ${p1} ${p2}`} fill={c} />
      {label && (
        <text x={bx + labelOffset[0]} y={by + labelOffset[1]} fill={c} fontSize={14} fontWeight={600}>
          {label}
        </text>
      )}
    </g>
  )
}

export function Txt({ x, y, children, color = 'ink', size = 12.5, anchor = 'start', dx = 0, dy = 0, weight = 500, italic = false }: { x: number; y: number; children: ReactNode; color?: Role | string; size?: number; anchor?: 'start' | 'middle' | 'end'; dx?: number; dy?: number; weight?: number; italic?: boolean }) {
  const { sx, sy } = usePlot()
  return (
    <text x={sx(x) + dx} y={sy(y) + dy} fill={colorOf(color)} fontSize={size} textAnchor={anchor} fontWeight={weight} fontStyle={italic ? 'italic' : undefined} style={{ paintOrder: 'stroke', stroke: 'var(--gd-surface)', strokeWidth: 3.5, strokeLinejoin: 'round' }}>
      {children}
    </text>
  )
}

export function Polyline({ points, color = 'ink', width = 2, dashed, opacity = 1, fill, closed = false }: Stroke & { points: [number, number][]; fill?: string; closed?: boolean }) {
  const { sx, sy } = usePlot()
  if (points.length === 0) return null
  const d = points.map(([px, py], i) => `${i ? 'L' : 'M'}${sx(px).toFixed(2)},${sy(py).toFixed(2)}`).join('') + (closed ? 'Z' : '')
  return <path d={d} fill={fill ?? 'none'} stroke={colorOf(color)} strokeWidth={width} strokeDasharray={dashed ? '5 5' : undefined} opacity={opacity} strokeLinejoin="round" strokeLinecap="round" />
}

export function Rect({ x, y, w, h, fill = 'var(--gd-blue)', opacity = 0.18, stroke, rx = 0 }: { x: number; y: number; w: number; h: number; fill?: string; opacity?: number; stroke?: string; rx?: number }) {
  const { sx, sy } = usePlot()
  const px = Math.min(sx(x), sx(x + w))
  const py = Math.min(sy(y), sy(y + h))
  return <rect x={px} y={py} width={Math.abs(sx(x + w) - sx(x))} height={Math.abs(sy(y + h) - sy(y))} fill={fill} fillOpacity={opacity} stroke={stroke} rx={rx} />
}

export function Dot({ x, y, r = 5, color = 'ink', hollow = false, opacity = 1 }: { x: number; y: number; r?: number; color?: Role | string; hollow?: boolean; opacity?: number }) {
  const { sx, sy } = usePlot()
  const c = colorOf(color)
  return <circle cx={sx(x)} cy={sy(y)} r={r} fill={hollow ? 'var(--gd-surface)' : c} stroke={c} strokeWidth={hollow ? 2 : 0} opacity={opacity} />
}

/**
 * A draggable point. Constrain with `lockX` / `lockY` or a custom `constrain`.
 * Arrow keys nudge it, so it's usable without a pointer.
 */
export function Pt({
  x,
  y,
  onDrag,
  color = 'accent',
  r = 7,
  label,
  lockX = false,
  lockY = false,
  constrain,
  snap,
  pulse = false,
  ariaLabel = 'Draggable point',
}: {
  x: number
  y: number
  onDrag: (x: number, y: number) => void
  color?: Role | string
  r?: number
  label?: ReactNode
  lockX?: boolean
  lockY?: boolean
  constrain?: (x: number, y: number) => [number, number]
  snap?: number
  pulse?: boolean
  ariaLabel?: string
}) {
  const s = usePlot()
  const svgRef = useContext(PlotSvgCtx)
  const c = colorOf(color)
  const dragging = useRef(false)

  function apply(nx: number, ny: number) {
    let px = lockX ? x : Math.min(s.x[1], Math.max(s.x[0], nx))
    let py = lockY ? y : Math.min(s.y[1], Math.max(s.y[0], ny))
    if (snap) {
      px = lockX ? px : Math.round(px / snap) * snap
      py = lockY ? py : Math.round(py / snap) * snap
    }
    if (constrain) [px, py] = constrain(px, py)
    onDrag(+px.toFixed(6), +py.toFixed(6))
  }

  function fromEvent(e: React.PointerEvent) {
    const p = clientToSvg(svgRef?.current, e.clientX, e.clientY)
    if (p) apply(s.ix(p.x), s.iy(p.y))
  }

  const stepX = snap ?? (s.x[1] - s.x[0]) / 100
  const stepY = snap ?? (s.y[1] - s.y[0]) / 100

  return (
    <g
      className="gd-drag"
      tabIndex={0}
      role="slider"
      aria-label={ariaLabel}
      aria-valuetext={`${fmt(x)}, ${fmt(y)}`}
      onPointerDown={e => {
        e.stopPropagation()
        ;(e.currentTarget as Element).setPointerCapture(e.pointerId)
        dragging.current = true
      }}
      onPointerMove={e => dragging.current && fromEvent(e)}
      onPointerUp={e => {
        dragging.current = false
        ;(e.currentTarget as Element).releasePointerCapture(e.pointerId)
      }}
      onKeyDown={e => {
        const k = e.shiftKey ? 5 : 1
        if (e.key === 'ArrowLeft') apply(x - stepX * k, y)
        else if (e.key === 'ArrowRight') apply(x + stepX * k, y)
        else if (e.key === 'ArrowUp') apply(x, y + stepY * k)
        else if (e.key === 'ArrowDown') apply(x, y - stepY * k)
        else return
        e.preventDefault()
      }}
    >
      {pulse && <circle cx={s.sx(x)} cy={s.sy(y)} r={r * 2.2} fill={c} className="gd-pulse" />}
      <circle className="gd-drag-halo" cx={s.sx(x)} cy={s.sy(y)} r={r * 2.4} fill={c} />
      <circle cx={s.sx(x)} cy={s.sy(y)} r={r} fill={c} stroke="var(--gd-surface)" strokeWidth={2.5} />
      {label && (
        <text x={s.sx(x) + r + 7} y={s.sy(y) - r - 4} fontSize={13} fontWeight={600} fill={c} style={{ paintOrder: 'stroke', stroke: 'var(--gd-surface)', strokeWidth: 4, strokeLinejoin: 'round' }}>
          {label}
        </text>
      )}
    </g>
  )
}
